/* JD galerija proizvoda – mobilni slajder (strelice + tačkice + swipe).
   - Radi samo do 749px (Dawn breakpoint); desktop ostaje netaknut.
   - Beskonačan: sa poslednje slike "desno" vodi na prvu (i dalje klizi udesno),
     sa prve "levo" na poslednju. Prelaz preko kraja animira privremeni sloj
     sa kopijama dve slike (transform), pa se lista neprimetno vrati na cilj.
   - Adaptivna pozadina strelice: meri se prosečna svetlina slike ispod svake
     strelice (mali canvas, jednom po slici, keširano). Ako merenje ne uspe,
     strelice uvek imaju blagu pozadinu. */
if (!customElements.get('jd-gallery-nav')) {
  customElements.define(
    'jd-gallery-nav',
    class JdGalleryNav extends HTMLElement {
      connectedCallback() {
        this.sliderComponent = this.closest('slider-component');
        this.list = this.sliderComponent && this.sliderComponent.querySelector('.product__media-list');
        if (!this.list || this.initialized) return;
        this.initialized = true;

        this.prevButton = this.querySelector('.jd-gnav__arrow--prev');
        this.nextButton = this.querySelector('.jd-gnav__arrow--next');
        this.dotsWrap = this.querySelector('.jd-gnav__dots');
        this.mql = window.matchMedia('(max-width: 749px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.cache = new Map();
        this.queue = [];
        this.measureFailed = false;
        this.index = 0;
        this.slides = [];

        this.prevButton.addEventListener('click', () => this.step(-1));
        this.nextButton.addEventListener('click', () => this.step(1));
        this.dotsWrap.addEventListener('click', (event) => {
          const dot = event.target.closest('[data-index]');
          if (dot) this.goTo(Number(dot.dataset.index));
        });

        this.onScroll = this.onScroll.bind(this);
        this.list.addEventListener('scroll', this.onScroll, { passive: true });

        // Swipe preko ivice (na prvoj/poslednjoj slici) takođe ide u krug.
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.list.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.list.addEventListener('touchend', this.onTouchEnd, { passive: true });

        // Izbor varijante u Dawn-u premešta slajdove (prepend) – tada sve iznova.
        this.mutationObserver = new MutationObserver(() => this.build());
        this.mutationObserver.observe(this.list, { childList: true });

        let lastWidth = 0;
        this.resizeObserver = new ResizeObserver(() => {
          this.position();
          const width = this.list.clientWidth;
          if (width && width !== lastWidth) {
            lastWidth = width;
            this.cache.clear();
            this.scheduleMeasurements();
          }
        });
        this.resizeObserver.observe(this.list);

        this.onBreakpoint = () => this.build();
        this.mql.addEventListener('change', this.onBreakpoint);

        this.build();
        this.startMeasuringAfterFirstImage();
      }

      disconnectedCallback() {
        if (!this.initialized) return;
        this.list.removeEventListener('scroll', this.onScroll);
        this.list.removeEventListener('touchstart', this.onTouchStart);
        this.list.removeEventListener('touchend', this.onTouchEnd);
        this.mutationObserver.disconnect();
        this.resizeObserver.disconnect();
        this.mql.removeEventListener('change', this.onBreakpoint);
        this.initialized = false;
      }

      build() {
        this.slides = Array.from(this.list.children).filter(
          (slide) => slide.matches('.product__media-item') && getComputedStyle(slide).display !== 'none'
        );
        const count = this.slides.length;
        this.classList.toggle('is-single', count < 2);
        this.sliderComponent.classList.toggle('jd-gallery--single', count < 2);

        this.dotsWrap.innerHTML = '';
        if (count > 1) {
          const template = this.dataset.dotLabel || 'Slika [index] od [count]';
          this.slides.forEach((slide, i) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'jd-gnav__dot';
            dot.dataset.index = i;
            dot.setAttribute('aria-controls', this.list.id);
            dot.setAttribute('aria-label', template.replace('[index]', i + 1).replace('[count]', count));
            this.dotsWrap.appendChild(dot);
          });
        }

        this.index = this.nearestIndex();
        this.position();
        this.update();
        this.scheduleMeasurements();
      }

      // Strelice su vertikalno centrirane na slici (ne na slici + tačkicama).
      position() {
        const height = this.list.offsetHeight;
        if (height) this.style.setProperty('--jd-arrow-top', `${Math.round(height * 0.5)}px`);
      }

      nearestIndex() {
        const left = this.list.scrollLeft;
        let best = 0;
        let bestDistance = Infinity;
        this.slides.forEach((slide, i) => {
          const distance = Math.abs(slide.offsetLeft - this.slideOffset() - left);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = i;
          }
        });
        return best;
      }

      // offsetLeft slajda je relativan na slider-component; oduzimamo poziciju liste.
      slideOffset() {
        return this.list.offsetLeft;
      }

      onScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          // Tokom animacije strelica/tačkica stanje je već postavljeno na ciljnu sliku.
          if (this.animationFrame) return;
          const index = this.nearestIndex();
          if (index !== this.index) {
            this.index = index;
            this.update();
          }
        });
      }

      step(direction) {
        const last = this.slides.length - 1;
        if (last < 1 || this.wrapping) return;
        if (direction > 0 && this.index >= last) this.wrapTo(0, 1);
        else if (direction < 0 && this.index <= 0) this.wrapTo(last, -1);
        else this.goTo(this.index + direction);
      }

      onTouchStart(event) {
        const touch = event.touches[0];
        const max = this.list.scrollWidth - this.list.clientWidth;
        this.touch = {
          x: touch.clientX,
          y: touch.clientY,
          atStart: this.list.scrollLeft <= 1,
          atEnd: this.list.scrollLeft >= max - 1,
        };
      }

      onTouchEnd(event) {
        if (!this.touch || this.wrapping) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - this.touch.x;
        const dy = touch.clientY - this.touch.y;
        const last = this.slides.length - 1;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          if (dx < 0 && this.touch.atEnd && this.index === last) this.wrapTo(0, 1);
          else if (dx > 0 && this.touch.atStart && this.index === 0) this.wrapTo(last, -1);
        }
        this.touch = null;
      }

      // Prelaz preko kraja: sloj sa kopijama trenutne i ciljne slike klizi
      // (transform) u istom smeru, lista se ispod skokom postavi na cilj.
      wrapTo(target, direction) {
        const from = this.slides[this.index];
        const to = this.slides[target];
        if (!from || !to || from === to) return;
        this.stopAnimation();
        this.index = target;
        this.update();
        const left = to.offsetLeft - this.slideOffset();
        if (this.reducedMotion.matches || !Element.prototype.animate) {
          this.list.scrollLeft = left;
          return;
        }

        this.wrapping = true;
        const overlay = document.createElement('div');
        overlay.className = 'jd-gnav__wrap';
        overlay.setAttribute('aria-hidden', 'true');
        Object.assign(overlay.style, {
          left: `${this.list.offsetLeft}px`,
          top: `${this.list.offsetTop}px`,
          width: `${this.list.offsetWidth}px`,
          height: `${this.list.offsetHeight}px`,
        });
        const track = document.createElement('div');
        track.className = 'jd-gnav__wrap-track';
        (direction > 0 ? [from, to] : [to, from]).forEach((slide) => {
          const panel = document.createElement('div');
          panel.className = 'jd-gnav__wrap-panel';
          panel.appendChild(this.cloneMedia(slide));
          track.appendChild(panel);
        });
        overlay.appendChild(track);
        this.sliderComponent.insertBefore(overlay, this);

        const frames = direction > 0 ? ['translateX(0)', 'translateX(-50%)'] : ['translateX(-50%)', 'translateX(0)'];
        const animation = track.animate(
          frames.map((transform) => ({ transform })),
          { duration: 420, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'forwards' }
        );
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          this.list.scrollLeft = left;
          this.whenLoaded(to).then(() => {
            overlay.remove();
            this.wrapping = false;
          });
        };
        animation.finished.catch(() => {}).then(finish);
        // Sigurnost: ako je tab sakriven, animacija ne napreduje – ne smemo da se zaglavimo.
        setTimeout(() => {
          if (!done) animation.finish();
          finish();
        }, 700);
      }

      // Samo vizuelna kopija slike (bez id-jeva, dugmadi i lightbox-a).
      cloneMedia(slide) {
        const container = slide.querySelector('.product-media-container');
        const media = container && container.querySelector('.product__media');
        const wrapper = container ? container.cloneNode(false) : document.createElement('div');
        wrapper.removeAttribute('id');
        if (media) {
          const copy = media.cloneNode(true);
          copy.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
          copy.querySelectorAll('img').forEach((img) => (img.loading = 'eager'));
          wrapper.appendChild(copy);
        }
        return wrapper;
      }

      whenLoaded(slide) {
        const img = slide.querySelector('.product__media img');
        if (!img || (img.complete && img.naturalWidth)) return Promise.resolve();
        return Promise.race([
          new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          }),
          new Promise((resolve) => setTimeout(resolve, 800)),
        ]);
      }

      goTo(index) {
        if (!this.slides.length || this.wrapping) return;
        index = Math.max(0, Math.min(this.slides.length - 1, index));
        this.index = index;
        this.update();
        this.animateTo(this.slides[index].offsetLeft - this.slideOffset());
      }

      // Sopstvena animacija (~350ms, easing) – nativni smooth scroll ume da traje i 1-2s.
      animateTo(left) {
        this.stopAnimation();
        const list = this.list;
        const start = list.scrollLeft;
        const distance = left - start;
        if (this.reducedMotion.matches || Math.abs(distance) < 1) {
          list.scrollLeft = left;
          return;
        }
        const duration = 350;
        const startTime = performance.now();
        const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
        list.style.scrollSnapType = 'none';
        this.cancelOnTouch = () => this.stopAnimation();
        list.addEventListener('touchstart', this.cancelOnTouch, { passive: true, once: true });
        const step = (now) => {
          const progress = Math.min(1, (now - startTime) / duration);
          list.scrollLeft = start + distance * ease(progress);
          if (progress < 1) this.animationFrame = requestAnimationFrame(step);
          else this.stopAnimation();
        };
        this.animationFrame = requestAnimationFrame(step);
      }

      stopAnimation() {
        if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
        if (this.cancelOnTouch) this.list.removeEventListener('touchstart', this.cancelOnTouch);
        this.cancelOnTouch = null;
        this.list.style.scrollSnapType = '';
      }

      update() {
        // Beskonačan slajder: strelice su uvek aktivne kad ima 2+ slike.
        this.prevButton.disabled = false;
        this.nextButton.disabled = false;
        this.dotsWrap.querySelectorAll('.jd-gnav__dot').forEach((dot, i) => {
          if (i === this.index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this.applyContrast();
      }

      applyContrast() {
        const slide = this.slides[this.index];
        if (!slide) return;
        if (this.measureFailed) {
          this.setBackground(true, true);
          return;
        }
        const result = this.cache.get(slide.dataset.mediaId);
        // Dok merenje ne stigne, zadržavamo trenutno stanje (bez "škljocanja").
        if (result) this.setBackground(result.prev, result.next);
      }

      setBackground(prev, next) {
        this.prevButton.classList.toggle('jd-gnav__arrow--bg', prev);
        this.nextButton.classList.toggle('jd-gnav__arrow--bg', next);
      }

      /* ---------- Merenje kontrasta ---------- */

      startMeasuringAfterFirstImage() {
        const firstImage = this.slides[0] && this.slides[0].querySelector('.product__media img');
        const start = () => this.idle(() => this.processQueue());
        if (!firstImage || (firstImage.complete && firstImage.naturalWidth)) start();
        else {
          firstImage.addEventListener('load', start, { once: true });
          firstImage.addEventListener('error', start, { once: true });
        }
        this.ready = true;
      }

      scheduleMeasurements() {
        if (!this.mql.matches || this.slides.length < 2) return;
        // Trenutna slika prva, pa susedne, pa ostale.
        const order = this.slides
          .map((slide, i) => ({ slide, distance: Math.abs(i - this.index) }))
          .sort((a, b) => a.distance - b.distance)
          .map((item) => item.slide)
          .filter((slide) => !this.cache.has(slide.dataset.mediaId));
        this.queue = order;
        if (this.ready && !this.processing) this.idle(() => this.processQueue());
      }

      idle(callback) {
        if ('requestIdleCallback' in window) requestIdleCallback(callback, { timeout: 1200 });
        else setTimeout(() => requestAnimationFrame(callback), 200);
      }

      async processQueue() {
        if (this.processing) return;
        this.processing = true;
        while (this.queue.length && !this.measureFailed) {
          const slide = this.queue.shift();
          const id = slide.dataset.mediaId;
          if (this.cache.has(id)) continue;
          try {
            const result = await this.measure(slide);
            if (result) this.cache.set(id, result);
          } catch (error) {
            this.measureFailed = true;
          }
          if (slide === this.slides[this.index]) this.applyContrast();
          await new Promise((resolve) => this.idle(resolve));
        }
        this.processing = false;
        if (this.measureFailed) this.applyContrast();
      }

      loadSample(src) {
        return new Promise((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.decoding = 'async';
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = src;
        });
      }

      async measure(slide) {
        const img = slide.querySelector('.product__media img');
        if (!img || !this.mql.matches) return null;
        const src = img.currentSrc || img.src;
        if (!src) return null;

        const url = new URL(src, window.location.href);
        url.searchParams.set('width', '160');
        const sample = await this.loadSample(url.href);

        // Geometrija: kutija slike unutar slajda i pozicija strelica unutar liste.
        const slideRect = slide.getBoundingClientRect();
        const boxRect = img.getBoundingClientRect();
        const listRect = this.list.getBoundingClientRect();
        if (!boxRect.width || !boxRect.height) return null;

        const scale = 120 / boxRect.width;
        const width = 120;
        const height = Math.max(1, Math.round(boxRect.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', { willReadFrequently: true });

        // Pozadina iza "contain" slike = pozadina kontejnera / stranice.
        context.fillStyle = this.backgroundOf(slide);
        context.fillRect(0, 0, width, height);

        const fit = getComputedStyle(img).objectFit;
        const ratio = sample.naturalWidth / sample.naturalHeight;
        let drawWidth = width;
        let drawHeight = width / ratio;
        if ((fit === 'cover' && drawHeight < height) || (fit !== 'cover' && drawHeight > height)) {
          drawHeight = height;
          drawWidth = height * ratio;
        }
        context.drawImage(sample, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);

        const boxLeftInSlide = boxRect.left - slideRect.left;
        const region = (button) => {
          const rect = button.querySelector('.jd-gnav__icon').getBoundingClientRect();
          return {
            x: (rect.left - listRect.left - boxLeftInSlide) * scale,
            y: (rect.top - boxRect.top) * scale,
            w: rect.width * scale,
            h: rect.height * scale,
          };
        };

        const arrowLuminance = this.luminance(this.parseColor(getComputedStyle(this.prevButton).color));
        return {
          prev: this.needsBackground(context, region(this.prevButton), arrowLuminance, width, height),
          next: this.needsBackground(context, region(this.nextButton), arrowLuminance, width, height),
        };
      }

      needsBackground(context, region, arrowLuminance, width, height) {
        const x = Math.max(0, Math.floor(region.x));
        const y = Math.max(0, Math.floor(region.y));
        const w = Math.min(width - x, Math.ceil(region.w));
        const h = Math.min(height - y, Math.ceil(region.h));
        if (w <= 0 || h <= 0) return false;

        // getImageData baca SecurityError ako je canvas "zaprljan" (CORS) – hvata se iznad.
        const data = context.getImageData(x, y, w, h).data;
        let sum = 0;
        let lowContrast = 0;
        const pixels = data.length / 4;
        for (let i = 0; i < data.length; i += 4) {
          const lum = this.luminance([data[i], data[i + 1], data[i + 2]]);
          sum += lum;
          if (this.contrast(lum, arrowLuminance) < 2) lowContrast++;
        }
        const average = sum / pixels;
        return this.contrast(average, arrowLuminance) < 3 || lowContrast / pixels > 0.35;
      }

      backgroundOf(element) {
        let node = element.querySelector('.product-media-container') || element;
        while (node && node !== document.documentElement) {
          const color = getComputedStyle(node).backgroundColor;
          const parsed = this.parseColor(color, true);
          if (parsed && parsed[3] > 0) return color;
          node = node.parentElement;
        }
        return '#fff';
      }

      parseColor(value, withAlpha) {
        const match = value && value.match(/[\d.]+/g);
        if (!match) return withAlpha ? null : [0, 0, 0];
        const rgb = match.slice(0, 3).map(Number);
        if (withAlpha) rgb.push(match[3] != null ? Number(match[3]) : 1);
        return rgb;
      }

      luminance([r, g, b]) {
        const channel = (c) => {
          c /= 255;
          return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        };
        return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
      }

      contrast(a, b) {
        return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
      }
    }
  );
}
